// components
import { DateNavigation } from 'components/shared';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import DutyRoundsDialog from './DutyRoundsDialog';
import { Container } from '@mui/material';
import Chip from '@sdb/ui/atoms/Chip';

// hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavParams } from 'hooks';

// types
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import { AskedDutyResponse } from 'generated/index';

// utils
import dayjs from 'dayjs';
import { formatDateTime, formatDate, flattenToDate } from 'utils/date';
import { queries } from 'RestAPIs/rapini';
import { getAskedDutiesByRequestId } from './utils';
import { DateNavigationContainer } from './AutoAskDutyRoundsOverview.styles';

const AutoAskDutyRoundsOverview = () => {
  const { t } = useTranslation('common');
  const { params } = useNavParams();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const { data: askedDuties, isLoading } = queries.useGetAskedDuties(
    dayjs(selectedDate).format('YYYY-MM-DD'),
    dayjs(selectedDate).format('YYYY-MM-DD'),
    undefined
  );

  const [selectedAskedDuty, setSelectedAskedDuty] =
    useState<AskedDutyResponse | null>(null);

  const onViewClick = (askedDutyId: number) => {
    const askedDuty = askedDuties?.find(
      (d) => d.duty.requestId === askedDutyId
    );
    if (askedDuty) {
      setSelectedAskedDuty(askedDuty);
    }
  };

  const handleClose = () => {
    setSelectedAskedDuty(null);
  };

  const askedDutyStatusColor = {
    ASKED: {
      label: t('askedDutyStatus.asked'),
      variant: 'tonal',
      color: 'default'
    },
    PLANNED: {
      label: t('askedDutyStatus.resolved'),
      variant: 'tonal',
      color: 'success'
    },
    ONE_REPLIED_AVAILABLE: {
      label: t('askedDutyStatus.available'),
      variant: 'filled',
      color: 'success'
    },
    ALL_REPLIED_NOT_AVAILABLE: {
      label: t('askedDutyStatus.notAvailable'),
      variant: 'filled',
      color: 'warning'
    },
    FAILED: {
      label: t('askedDutyStatus.notResolved'),
      variant: 'tonal',
      color: 'error'
    },
    OPEN: {
      label: t('askedDutyStatus.open'),
      variant: 'filled',
      color: 'default'
    },
    PARKED: {
      label: t('askedDutyStatus.parked'),
      variant: 'tonal',
      color: 'warning'
    }
  };

  const renderAskedDutyStatusColumn = (params) => {
    const value = params.value;
    const statusConfig =
      askedDutyStatusColor[value] || askedDutyStatusColor.OPEN;
    return (
      <Chip
        label={statusConfig.label}
        size="medium"
        variant={statusConfig.variant}
        color={statusConfig.color}
      />
    );
  };

  const columns: GridColDef<GridValidRowModel>[] = [
    { field: 'id', headerName: t('number'), minWidth: 100 },
    {
      field: 'date',
      headerName: t('date'),
      minWidth: 80,
      renderCell: ({ value }) => formatDate(value)
    },
    { field: 'start', headerName: t('startTime'), minWidth: 70 },
    { field: 'end', headerName: t('endTime'), minWidth: 70 },
    { field: 'dutyCode', headerName: t('dutyCode'), minWidth: 80 },
    {
      field: 'organizationalUnit',
      headerName: t('organizationalUnit'),
      flex: 1
    },
    { field: 'function', headerName: t('function'), flex: 1 },
    { field: 'numberOfAskedEmployees', headerName: t('asked'), minWidth: 40 },
    {
      field: 'numberOfNotifiedEmployees',
      headerName: t('notified'),
      minWidth: 40
    },
    {
      field: 'numberOfRespondedEmployees',
      headerName: t('responded'),
      minWidth: 40
    },
    { field: 'employee', headerName: t('employee'), flex: 1 },
    {
      field: 'triggeredAt',
      headerName: t('triggeredAt'),
      minWidth: 120,
      renderCell: ({ value }) => formatDateTime(value)
    },
    {
      field: 'status',
      headerName: t('status'),
      minWidth: 140,
      renderCell: (params) => renderAskedDutyStatusColumn(params)
    }
  ];

  const rows =
    askedDuties
      ?.sort(
        (a, b) =>
          new Date(a.duty.start).getTime() - new Date(b.duty.start).getTime()
      )
      .map((d) => ({
        id: d.duty.requestId,
        employee: d.duty.assignedEmployee?.nameInformal,
        date: d.duty.start,
        start: dayjs(d.duty.start).format('HH:mm'),
        end: dayjs(d.duty.end).format('HH:mm'),
        dutyCode: d.duty.dutyCode?.dutyCode,
        organizationalUnit: d.duty.assignedOrganizationalUnit?.name,
        function: d.duty.function?.description,
        triggeredAt: d.triggeredAt,
        numberOfAskedEmployees: d.numberOfAskedEmployees,
        numberOfNotifiedEmployees: d.numberOfNotifiedEmployees,
        numberOfRespondedEmployees: d.numberOfRespondedEmployees,
        status: d.status
      })) ?? [];

  // Open duty modal based on query params passed to page
  useEffect(() => {
    const requestId = params?.request;

    if (requestId && (askedDuties || []).length > 0) {
      setSelectedAskedDuty(getAskedDutiesByRequestId(askedDuties, requestId));
    }
  }, [params?.request]);

  // Set data navigation based on query params passed to page
  useEffect(() => {
    const date =
      params?.date && dayjs(params.date, 'DD-MM-YYYY').isValid()
        ? dayjs(params.date, 'DD-MM-YYYY').toDate()
        : null;

    if (date) setSelectedDate(date);
  }, [params?.date]);

  return (
    <Container>
      <DateNavigationContainer>
        <DateNavigation
          defaultPeriod="day"
          defaultDate={selectedDate}
          onDateChange={(date) => setSelectedDate(flattenToDate(date))}
        />
      </DateNavigationContainer>
      <TableCRUDLayout
        columns={columns}
        rows={rows}
        editable={false}
        loading={isLoading}
        customActions={(id) => [
          {
            tooltipTitle: t('view'),
            icon: 'eye',
            onClick: () => onViewClick(id as number)
          }
        ]}
      />
      {selectedAskedDuty && (
        <DutyRoundsDialog
          open={!!selectedAskedDuty}
          askedDuty={selectedAskedDuty}
          handleClose={handleClose}
        />
      )}
    </Container>
  );
};

export default AutoAskDutyRoundsOverview;
